// Components/Pages/BlogDetail.jsx
import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { 
  FaArrowLeft, 
  FaUser, 
  FaCalendarAlt, 
  FaClock, 
  FaSpinner,
  FaFacebook,
  FaTwitter,
  FaLinkedin,
  FaEnvelope,
  FaTag,
  FaShareAlt
} from "react-icons/fa";

const API_BASE = 'https://api.rigokenya.org';

function BlogDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBlogPost();
    window.scrollTo(0, 0);
  }, [id]);

  const fetchBlogPost = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/blog/${id}`, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch blog post');
      }

      const data = await response.json();
      
      // DEBUG: Log the entire response to see what fields exist
      console.log('📝 Full API Response:', JSON.stringify(data, null, 2));
      console.log('🔍 Available fields:', Object.keys(data));
      console.log('📄 Content field value:', data.content);
      console.log('📄 Body field value:', data.body);
      console.log('📄 Excerpt field value:', data.excerpt);
      
      // Try to find content in any possible field
      const content = data.body || data.content || data.excerpt || data.full_story || data.text || data.description || '';
      
      console.log('✅ Found content:', content.substring(0, 200) + '...');
      
      const processedPost = {
        ...data,
        read_time: data.read_time || data.readTime || '3 min read',
        fullContent: content,
        body: content,
        content: content,
        excerpt: content.substring(0, 300) + (content.length > 300 ? '...' : ''),
        tags: Array.isArray(data.tags) ? data.tags : (data.tags ? data.tags.split(',') : []),
        image: data.image ? (
          data.image.startsWith('/uploads/') ? `${API_BASE}${data.image}` : data.image
        ) : null,
        category: data.category || 'Uncategorized',
        author: data.author || 'Unknown',
        date: data.date || '',
        featured: data.featured || false
      };
      
      console.log('✅ Processed Post:', processedPost);
      setPost(processedPost);
      setError(null);
    } catch (err) {
      console.error('❌ Error fetching blog post:', err);
      setError(err.message);
      setPost(null);
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('/uploads/')) {
      return `${API_BASE}${imagePath}`;
    }
    if (imagePath.startsWith('http')) {
      return imagePath;
    }
    return imagePath;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const shareUrl = window.location.href;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center animate-pulse">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full border-4 border-primaryGold border-t-transparent animate-spin"></div>
          <p className="text-gray-600 font-medium">Loading article...</p>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-6">
          <div className="text-7xl mb-6">📖</div>
          <h2 className="text-3xl font-bold text-gray-800 mb-3">Post Not Found</h2>
          <p className="text-gray-600 mb-8">The blog post you're looking for doesn't exist or has been removed.</p>
          <Link 
            to="/blog" 
            className="inline-flex items-center gap-2 bg-primaryBlue text-white px-8 py-3 rounded-xl hover:bg-primaryBlue/90 transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            <FaArrowLeft /> Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-8 md:py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <button
          onClick={() => navigate('/blog')}
          className="flex items-center gap-2 text-gray-500 hover:text-primaryBlue transition-all duration-300 mb-8 group"
        >
          <div className="p-2 rounded-full bg-white shadow-sm group-hover:shadow-md transition-all duration-300">
            <FaArrowLeft className="text-sm group-hover:-translate-x-1 transition-transform duration-300" />
          </div>
          <span className="font-medium">Back to Blog</span>
        </button>

        {/* Blog Post */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden animate-fadeIn">
          {/* Featured Image */}
          {post.image && (
            <div className="relative w-full h-72 md:h-[450px] overflow-hidden">
              <img 
                src={getImageUrl(post.image)}
                alt={post.title}
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent"></div>
            </div>
          )}

          <div className="p-6 md:p-10 lg:p-12">
            {/* Category */}
            {post.category && post.category !== 'Uncategorized' && (
              <span className="inline-block px-4 py-1.5 bg-primaryGold/10 text-primaryGold text-sm font-semibold rounded-full mb-5 border border-primaryGold/20">
                {post.category}
              </span>
            )}

            {/* Title */}
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-5 leading-tight">
              {post.title}
            </h1>

            {/* Meta Info */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-6 pb-6 border-b border-gray-100">
              <div className="flex items-center gap-2 bg-gray-50 px-3 py-1.5 rounded-full">
                <div className="w-6 h-6 rounded-full bg-primaryGold/20 flex items-center justify-center">
                  <FaUser className="text-primaryGold text-xs" />
                </div>
                <span className="font-medium text-gray-700">{post.author || 'Unknown'}</span>
              </div>
              {post.date && (
                <div className="flex items-center gap-2 bg-gray-50 px-3 py-1.5 rounded-full">
                  <FaCalendarAlt className="text-primaryGold text-xs" />
                  <span className="text-gray-600">{formatDate(post.date)}</span>
                </div>
              )}
              <div className="flex items-center gap-2 bg-gray-50 px-3 py-1.5 rounded-full">
                <FaClock className="text-primaryGold text-xs" />
                <span className="text-gray-600">{post.read_time}</span>
              </div>
            </div>

            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-8">
                {post.tags.map((tag, index) => (
                  <span 
                    key={index} 
                    className="flex items-center gap-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-600 px-3 py-1.5 rounded-full transition-colors duration-200 cursor-default"
                  >
                    <FaTag className="text-primaryGold text-[10px]" />
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {/* Content - Display FULL content without truncation */}
            <div className="prose prose-lg max-w-none prose-headings:text-gray-800 prose-p:text-gray-700 prose-p:text-justify prose-a:text-primaryBlue prose-a:no-underline hover:prose-a:underline prose-blockquote:border-primaryGold prose-blockquote:bg-gray-50 prose-blockquote:p-4 prose-blockquote:rounded-lg prose-img:rounded-xl">
              {post.fullContent ? (
                <div dangerouslySetInnerHTML={{ __html: post.fullContent }} />
              ) : post.content ? (
                <div dangerouslySetInnerHTML={{ __html: post.content }} />
              ) : post.body ? (
                <div dangerouslySetInnerHTML={{ __html: post.body }} />
              ) : (
                <div dangerouslySetInnerHTML={{ __html: post.excerpt || 'No content available.' }} />
              )}
            </div>

            {/* Share Section */}
            <div className="mt-12 pt-8 border-t border-gray-200">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <FaShareAlt className="text-primaryGold" />
                    Share this story
                  </h4>
                  <p className="text-xs text-gray-400 mt-1">Spread the word about this article</p>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <a
                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-3 bg-[#1877f2] text-white rounded-xl hover:bg-[#1877f2]/80 transition-all duration-300 hover:scale-105 hover:shadow-lg"
                    aria-label="Share on Facebook"
                  >
                    <FaFacebook className="text-lg" />
                  </a>
                  <a
                    href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(post.title || '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-3 bg-[#000000] text-white rounded-xl hover:bg-[#000000]/80 transition-all duration-300 hover:scale-105 hover:shadow-lg"
                    aria-label="Share on Twitter"
                  >
                    <FaTwitter className="text-lg" />
                  </a>
                  <a
                    href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-3 bg-[#0a66c2] text-white rounded-xl hover:bg-[#0a66c2]/80 transition-all duration-300 hover:scale-105 hover:shadow-lg"
                    aria-label="Share on LinkedIn"
                  >
                    <FaLinkedin className="text-lg" />
                  </a>
                  <a
                    href={`mailto:?subject=${encodeURIComponent(post.title || '')}&body=${encodeURIComponent(shareUrl)}`}
                    className="p-3 bg-[#ea4335] text-white rounded-xl hover:bg-[#ea4335]/80 transition-all duration-300 hover:scale-105 hover:shadow-lg"
                    aria-label="Share via Email"
                  >
                    <FaEnvelope className="text-lg" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </article>
      </div>

      {/* Add custom animation */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.6s ease-out;
        }
        /* Text justification for prose */
        .prose p {
          text-align: justify !important;
          text-justify: inter-word !important;
        }
        /* Better spacing for headings */
        .prose h2, .prose h3, .prose h4 {
          text-align: left !important;
        }
        /* Blockquote styling */
        .prose blockquote {
          text-align: left !important;
          font-style: italic;
        }
      `}</style>
    </div>
  );
}

export default BlogDetail;